import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FiltersButton, InfoBanner, ScrollToTopBtn, TableHeader } from "@services/ui-components";
import { useReactiveVar } from "@apollo/client";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import ProductsTable from "./ProductsTable";
import ProductsFilterWrapper from "./ProductsFilterWrapper";
import { ProductsFilter } from "../types";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import {
    isWarehouseTutorialActive,
    moveStepInLocalStorage,
    StepDirection,
} from "../../Joyride/WarehouseTutorial/joyrideStepHelpers";
import { StartWarehouseTutorialButton, StartWarehouseTutorialModal } from "../../index";
import { useCloseTooltipsOnModalOpen } from "../../../hooks/useCloseTooltipsOnModalOpen";
import { CompanyAccess, PermissionScope } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { useTutorialAccess } from "../../Joyride/useTutorialAccess";

const ProductsList: React.FC = () => {
    const { t, i18n } = useTranslation();
    const { branchId } = useParams();
    const navigate = useNavigate();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canCreateProduct = useRoleAccess(CompanyAccess.ProductCreate);
    const canCreateSaleOperation = useRoleAccess(CompanyAccess.WarehouseOperationSaleCreate, {
        scope: PermissionScope.Self,
    });
    const { canStartWarehouseTutorial } = useTutorialAccess();
    const INDONESIAN_LANGUAGE = "id";
    const isIndonesianLanguage = i18n.language === INDONESIAN_LANGUAGE;
    const [searchParams] = useSearchParams();
    const categoryId: number = Number(searchParams.get("id")) || null;
    const [showFilters, setShowFilters] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const tutorialActive = isWarehouseTutorialActive();
    const [showStartWarehouseTutorialModal, setShowStartWarehouseTutorialModal] = useState<boolean>(false);
    const [toggleProductSaleModal, setToggleProductSaleModal] = useState(false);
    const { tooltipResetKey, resetTooltips } = useCloseTooltipsOnModalOpen();

    const handleToggleProductSaleModal = () => setToggleProductSaleModal((prevState) => !prevState);
    const handleCloseStartWarehouseTutorialModal = () => {
        resetTooltips();
        setShowStartWarehouseTutorialModal(false);
    };

    const initialValueFilter: ProductsFilter = {
        nameSkuBarcode: null,
        categoryId: null,
        brand: null,
        stockStatus: null,
    };

    const [filters, setFilters] = useState<ProductsFilter>(initialValueFilter);

    const toggleFilters = useCallback(() => setShowFilters((prev) => !prev), []);

    useEffect(() => {
        categoryId && toggleFilters();
    }, [categoryId, toggleFilters]);
    const handleCreateProduct = () => {
        tutorialActive && moveStepInLocalStorage(StepDirection.NEXT);
        navigate(`/warehouse/${currentCompanyId}/new-product/branch/${branchId}`);
    };

    return (
        <div className="mb-9 px-2 px-lg-0">
            <InfoBanner>
                <p className="fs-7 mb-0">{t("Create and edit product categories and subcategories.")}</p>
            </InfoBanner>
            <TableHeader
                title={t("List of Products")}
                paths={[
                    {
                        path: `/warehouse/product/${currentCompanyId}/branch/${branchId}`,
                        label: t("Warehouse accounting"),
                    },
                    { path: `/warehouse/product/${currentCompanyId}/branch/${branchId}`, label: t("Products") },
                ]}
                colSize={5}
                btnColSize={7}
                buttons={
                    <div className="mt-3 mt-lg-0">
                        <Row
                            className="g-3 align-items-stretch flex-xxl-nowrap justify-content-md-end"
                            xs={1}
                            xxl="auto"
                        >
                            {canStartWarehouseTutorial && (
                                <>
                                    <Col
                                        xs={12}
                                        md={isIndonesianLanguage ? "auto" : 6}
                                        className={`d-sm-none d-md-flex d-xxl-none
                  ${isIndonesianLanguage && canCreateProduct ? "pe-md-1 pe-xxl-2" : ""}`}
                                    >
                                        <StartWarehouseTutorialButton
                                            classBtn={`w-100 text-truncate 
                   ${isIndonesianLanguage ? "px-lg-1" : ""}
                  `}
                                            handleClick={() => setShowStartWarehouseTutorialModal(true)}
                                        />
                                    </Col>
                                    <Col
                                        xs={12}
                                        sm="auto"
                                        className="d-none justify-content-end d-sm-flex d-md-none d-xxl-flex"
                                    >
                                        <StartWarehouseTutorialButton
                                            classBtn="w-auto px-1"
                                            iconOnly
                                            handleClick={() => setShowStartWarehouseTutorialModal(true)}
                                            tooltipResetKey={tooltipResetKey}
                                        />
                                    </Col>
                                </>
                            )}
                            <Col
                                xs={12}
                                sm
                                md
                                xl
                                xxl="auto"
                                className={`${isIndonesianLanguage && canCreateProduct ? "ps-md-1 ps-xxl-2" : ""}`}
                            >
                                <div className="d-grid d-xxl-block">
                                    {canCreateSaleOperation && (
                                        <Button
                                            variant="outline-orange"
                                            onClick={() => setToggleProductSaleModal(true)}
                                            className={`text-nowrap ${isIndonesianLanguage ? "px-md-1 px-xxl-2" : ""}`}
                                        >
                                            <i className="bi bi-cart4" />
                                            <span className="ms-2">{t("Sale of products")}</span>
                                        </Button>
                                    )}
                                </div>
                            </Col>
                            {canCreateProduct && (
                                <Col
                                    xs={12}
                                    sm
                                    md={6}
                                    xl={6}
                                    xxl="auto"
                                    className={`${isIndonesianLanguage ? "pe-md-1 pe-xxl-2" : ""}`}
                                >
                                    <div className="d-grid d-xxl-block">
                                        <Button
                                            variant="primary"
                                            className="text-white text-nowrap px-xxl-3"
                                            onClick={handleCreateProduct}
                                            data-tour="create-product-btn"
                                        >
                                            <i className="bi bi-plus-lg fw-bold me-1" />
                                            {t("Add Product")}
                                        </Button>
                                    </div>
                                </Col>
                            )}
                            <Col
                                xs={12}
                                sm="auto"
                                md={6}
                                xxl="auto"
                                className={` ${isIndonesianLanguage && canCreateProduct ? "ps-md-1 ps-xxl-2" : ""}`}
                            >
                                <div className="d-grid d-xxl-block">
                                    <FiltersButton
                                        filter={filters}
                                        showFilters={showFilters}
                                        onClick={toggleFilters}
                                        classes="text-nowrap px-4"
                                    />
                                </div>
                            </Col>
                        </Row>
                    </div>
                }
                showFilters={showFilters}
            >
                <ProductsFilterWrapper setFilters={setFilters} />
            </TableHeader>
            <ProductsTable
                filters={filters}
                isOpenProductSaleModal={toggleProductSaleModal}
                onToggleProductSaleModal={handleToggleProductSaleModal}
            />

            <ScrollToTopBtn />

            {showStartWarehouseTutorialModal && (
                <StartWarehouseTutorialModal
                    show={showStartWarehouseTutorialModal}
                    handleClose={handleCloseStartWarehouseTutorialModal}
                />
            )}

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
        </div>
    );
};

export default ProductsList;
